/**
 * Import people into a STATIC list from a CSV (or a spreadsheet).
 *
 * The file is parsed in the browser with SheetJS — loaded on demand, exactly as
 * the attachment viewer does — then mapped column by column, previewed, and
 * sent in chunks of at most 5,000 rows (the server's ceiling per request).
 *
 * ⚠️ Chunks are separate requests and there is NO rollback. If chunk three of
 * five fails, chunks one and two are in the list; the report says so rather
 * than claiming nothing was imported.
 *
 * An email already on a CRM contact is added as that CONTACT (the server's
 * rule, reported back as `matchedContacts`); suppressed emails are still added
 * and counted, because they are skipped at send time, not here.
 */
import { useMemo, useState } from 'react';
import {
  Button,
  Field,
  Input,
  Modal,
  Select,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { api, ApiError } from '@/lib/api';
import { CONTACT_NAME_LABELS, composeFullName, splitFullName } from '@/lib/contact-name';
import { pluralise } from '@/lib/format';
import type { ImportResult, ImportRow } from '@/lib/types';

// The server accepts 5000 rows per request, but the API caps JSON bodies at
// 1 MB, which a 5000-row chunk with names and companies can exceed (413).
// 1000 rows stays well under it.
const CHUNK = 1000;
const PREVIEW_ROWS = 10;

type Target = 'email' | 'firstName' | 'lastName' | 'name' | 'companyName' | 'country';

const TARGETS: { key: Target; label: string; pattern: RegExp }[] = [
  { key: 'email', label: 'Email', pattern: /e-?mail/i },
  { key: 'firstName', label: CONTACT_NAME_LABELS.firstName, pattern: /^(first|given|fore)[\s_-]*name$|^first$|^fname$/i },
  { key: 'lastName', label: CONTACT_NAME_LABELS.lastName, pattern: /^(last|sur|family)[\s_-]*name$|^surname$|^last$|^lname$/i },
  {
    key: 'name',
    label: CONTACT_NAME_LABELS.name,
    pattern: /^(full|contact|person|subscriber|display)?[\s_-]*name$|^fullname$/i,
  },
  { key: 'companyName', label: 'Company', pattern: /company|organi[sz]ation|institution|school|university|employer/i },
  { key: 'country', label: 'Country', pattern: /country/i },
];

type Mapping = Record<Target, number | null>;

const EMPTY_MAPPING: Mapping = {
  email: null,
  firstName: null,
  lastName: null,
  name: null,
  companyName: null,
  country: null,
};

function detectMapping(headers: string[]): Mapping {
  const mapping: Mapping = { ...EMPTY_MAPPING };
  const used = new Set<number>();
  for (const target of TARGETS) {
    const index = headers.findIndex((h, i) => !used.has(i) && target.pattern.test(h.trim()));
    if (index >= 0) {
      mapping[target.key] = index;
      used.add(index);
    }
  }
  return mapping;
}

interface Parsed {
  fileName: string;
  headers: string[];
  rows: string[][];
}

async function parseFile(file: File): Promise<Parsed> {
  const XLSX = await import('xlsx');
  const isCsv = /\.(csv|txt)$/i.test(file.name) || file.type === 'text/csv';
  const workbook = isCsv
    ? XLSX.read(await file.text(), { type: 'string', raw: true })
    : XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const firstName = workbook.SheetNames[0];
  const sheet = firstName ? workbook.Sheets[firstName] : undefined;
  if (!sheet) return { fileName: file.name, headers: [], rows: [] };
  const all = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: '' }) as unknown[][];
  const asText = all.map((row) => row.map((cell) => (cell == null ? '' : String(cell).trim())));
  const [head = [], ...body] = asText;
  return {
    fileName: file.name,
    headers: head,
    // A wholly blank line is not a row anybody meant to import.
    rows: body.filter((row) => row.some((cell) => cell !== '')),
  };
}

function cell(row: string[], index: number | null): string {
  if (index === null) return '';
  return (row[index] ?? '').trim();
}

/**
 * The three name fields of one row, each filling the others (owner,
 * 2026-10-16: "if one is filled other 2 get auto filled"). Subscribers store
 * only first + last, so a Full name column is SPLIT into them (last word = last
 * name, `splitFullName`): both parts blank → the whole split; one part present
 * and the full name starts / ends with it → the rest fills the blank part. A
 * full name that disagrees with a typed part never overwrites it.
 */
function resolveName(row: string[], mapping: Mapping): { firstName: string; lastName: string; name: string } {
  let firstName = cell(row, mapping.firstName).replace(/\s+/g, ' ');
  let lastName = cell(row, mapping.lastName).replace(/\s+/g, ' ');
  const full = cell(row, mapping.name).replace(/\s+/g, ' ');
  if (full !== '') {
    if (firstName === '' && lastName === '') {
      ({ firstName, lastName } = splitFullName(full));
    } else if (lastName === '' && full.toLowerCase().startsWith(`${firstName.toLowerCase()} `)) {
      lastName = full.slice(firstName.length + 1).trim();
    } else if (firstName === '' && full.toLowerCase().endsWith(` ${lastName.toLowerCase()}`)) {
      firstName = full.slice(0, full.length - lastName.length - 1).trim();
    }
  }
  return { firstName, lastName, name: full !== '' ? full : composeFullName(firstName, lastName) };
}

function previewCell(row: string[], mapping: Mapping, target: Target): string {
  if (target === 'firstName' || target === 'lastName' || target === 'name') return resolveName(row, mapping)[target];
  return cell(row, mapping[target]);
}

export function ImportSubscribersModal({
  open,
  listId,
  onClose,
  onImported,
}: {
  open: boolean;
  listId: number;
  onClose(): void;
  /** Called once anything may have been written, so the members table re-reads. */
  onImported(): void;
}) {
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping>(EMPTY_MAPPING);
  const [source, setSource] = useState('');
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const reset = () => {
    setParsed(null);
    setParseError(null);
    setMapping(EMPTY_MAPPING);
    setSource('');
    setSourceError(null);
    setProgress(null);
    setResult(null);
    setImportError(null);
  };

  const close = () => {
    if (importing) return;
    reset();
    onClose();
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setParsing(true);
    setParseError(null);
    setResult(null);
    setImportError(null);
    try {
      const next = await parseFile(file);
      if (next.headers.length === 0 || next.rows.length === 0) {
        setParsed(null);
        setParseError('That file has no rows to import. The first row must hold the column names.');
        return;
      }
      setParsed(next);
      setMapping(detectMapping(next.headers));
      if (!source) setSource(next.fileName.replace(/\.[^.]+$/, '').slice(0, 120));
    } catch {
      setParsed(null);
      setParseError('That file could not be read as a CSV or spreadsheet.');
    } finally {
      setParsing(false);
    }
  };

  const preview = useMemo(
    () => (parsed ? parsed.rows.slice(0, PREVIEW_ROWS) : []),
    [parsed],
  );

  const submit = async () => {
    if (!parsed || importing) return;
    if (mapping.email === null) return;
    const trimmedSource = source.trim();
    if (!trimmedSource) {
      setSourceError('Name where these people came from.');
      return;
    }
    setSourceError(null);
    setImporting(true);
    setImportError(null);
    setResult(null);

    // Countries are sent as ids, so a mapped country column is resolved against
    // the country master by name or code. An unmatched name is sent as null.
    let countryIds = new Map<string, number>();
    if (mapping.country !== null) {
      try {
        const countries = await api.listCountries({ perPage: 250 });
        countryIds = new Map(
          countries.data.flatMap((c) => {
            const entries: [string, number][] = [[c.countryName.toLowerCase(), c.id]];
            if (c.countryCode) entries.push([c.countryCode.toLowerCase(), c.id]);
            return entries;
          }),
        );
      } catch {
        setImporting(false);
        setImportError('The country list could not be loaded, so the import did not start. Unmap the country column or try again.');
        return;
      }
    }

    const rows: ImportRow[] = parsed.rows.map((row) => {
      const out: ImportRow = { email: cell(row, mapping.email) };
      const { firstName: first, lastName: last } = resolveName(row, mapping);
      const company = cell(row, mapping.companyName);
      if (first) out.firstName = first;
      if (last) out.lastName = last;
      if (company) out.companyName = company;
      if (mapping.country !== null) {
        const country = cell(row, mapping.country).toLowerCase();
        out.countryId = country ? (countryIds.get(country) ?? null) : null;
      }
      return out;
    });

    const total: ImportResult = {
      created: 0,
      updated: 0,
      addedToList: 0,
      alreadyInList: 0,
      matchedContacts: 0,
      suppressed: 0,
      invalid: [],
    };
    let done = 0;
    setProgress({ done: 0, total: rows.length });
    try {
      for (let start = 0; start < rows.length; start += CHUNK) {
        const chunk = rows.slice(start, start + CHUNK);
        const response = await api.importMarketingListRows(listId, { source: trimmedSource, rows: chunk });
        const r = response.data;
        total.created += r.created;
        total.updated += r.updated;
        total.addedToList += r.addedToList;
        total.alreadyInList += r.alreadyInList;
        total.matchedContacts += r.matchedContacts;
        total.suppressed += r.suppressed;
        total.invalid.push(...r.invalid.map((bad) => ({ ...bad, row: bad.row + start })));
        done += chunk.length;
        setProgress({ done, total: rows.length });
      }
      setResult(total);
    } catch (error) {
      const reason = error instanceof ApiError ? (error.formMessage ?? error.message) : 'The import failed.';
      setImportError(
        done > 0
          ? `${reason} The first ${pluralise(done, 'row')} were imported before this; the rest were not.`
          : reason,
      );
      if (done > 0) setResult(total);
    } finally {
      setImporting(false);
      if (done > 0) onImported();
    }
  };

  const headerOptions = parsed?.headers ?? [];

  return (
    <Modal
      open={open}
      onClose={close}
      title="Import CSV"
      size="lg"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={close} disabled={importing}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result && (
            <Button onClick={submit} loading={importing} disabled={!parsed || mapping.email === null}>
              {parsed ? `Import ${pluralise(parsed.rows.length, 'row')}` : 'Import'}
            </Button>
          )}
        </div>
      }
    >
      <div className="grid gap-4">
        {!result && (
          <Field label="File" error={parseError ?? undefined}>
            {(props) => (
              <Input
                {...props}
                type="file"
                accept=".csv,.txt,.xlsx,.xls,text/csv"
                disabled={parsing || importing}
                onChange={(e) => {
                  void onFile(e.target.files?.[0]);
                }}
              />
            )}
          </Field>
        )}

        {parsing && <p className="m-0 text-sm text-fg-muted">Reading the file…</p>}

        {parsed && !result && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              {TARGETS.map((target) => (
                <Field
                  key={target.key}
                  label={target.label}
                  required={target.key === 'email'}
                  error={target.key === 'email' && mapping.email === null ? 'Choose the column holding email addresses.' : undefined}
                >
                  {(props) => (
                    <Select
                      {...props}
                      value={mapping[target.key] === null ? '' : String(mapping[target.key])}
                      onChange={(e) =>
                        setMapping((prev) => ({
                          ...prev,
                          [target.key]: e.target.value === '' ? null : Number(e.target.value),
                        }))
                      }
                      disabled={importing}
                    >
                      <option value="">Not imported</option>
                      {headerOptions.map((h, i) => (
                        <option key={i} value={i}>
                          {h || `Column ${i + 1}`}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              ))}
              <Field label="Source" required error={sourceError ?? undefined}>
                {(props) => (
                  <Input
                    {...props}
                    value={source}
                    maxLength={120}
                    onChange={(e) => setSource(e.target.value)}
                    disabled={importing}
                    invalid={Boolean(sourceError)}
                  />
                )}
              </Field>
            </div>

            <div className="grid gap-1.5">
              <p className="m-0 text-xs text-fg-muted">
                First {Math.min(PREVIEW_ROWS, parsed.rows.length)} of {pluralise(parsed.rows.length, 'row')}
              </p>
              <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
                <Table className="min-w-[560px]">
                  <THead>
                    <TR>
                      {TARGETS.map((t) => (
                        <TH key={t.key}>{t.label}</TH>
                      ))}
                    </TR>
                  </THead>
                  <TBody>
                    {preview.map((row, i) => (
                      <TR key={i}>
                        {TARGETS.map((t) => (
                          <TD
                            key={t.key}
                            className="max-w-[12rem] truncate"
                            title={previewCell(row, mapping, t.key) || undefined}
                          >
                            {previewCell(row, mapping, t.key) || '—'}
                          </TD>
                        ))}
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            </div>
          </>
        )}

        {progress && importing && (
          <p className="m-0 text-sm text-fg-secondary tabular" role="status">
            Imported {progress.done} of {progress.total} rows…
          </p>
        )}

        {importError && (
          <Notice tone="danger" role="alert">
            {importError}
          </Notice>
        )}

        {result && <ImportReport result={result} />}
      </div>
    </Modal>
  );
}

function ImportReport({ result }: { result: ImportResult }) {
  const lines: [string, number][] = [
    ['Added to this list', result.addedToList],
    ['Already in this list', result.alreadyInList],
    ['New subscribers created', result.created],
    ['Existing subscribers updated', result.updated],
    ['Matched to CRM contacts', result.matchedContacts],
    ['Suppressed (kept, skipped at send)', result.suppressed],
    ['Invalid rows', result.invalid.length],
  ];
  return (
    <div className="grid gap-3">
      <dl className="m-0 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
        {lines.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-fg-secondary">{label}</dt>
            <dd className="m-0 text-right text-fg tabular">{value}</dd>
          </div>
        ))}
      </dl>
      {result.invalid.length > 0 && (
        <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
          <Table className="min-w-[480px]">
            <THead>
              <TR>
                <TH numeric>Row</TH>
                <TH>Email</TH>
                <TH>Reason</TH>
              </TR>
            </THead>
            <TBody>
              {result.invalid.map((bad, i) => (
                <TR key={`${bad.row}-${i}`}>
                  <TD numeric>{bad.row}</TD>
                  <TD className="max-w-[16rem] truncate" title={bad.email}>
                    {bad.email || '—'}
                  </TD>
                  <TD>{bad.reason}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      )}
    </div>
  );
}
